import { ref } from 'vue';
import { isPlatform } from '@ionic/vue';
import { Capacitor } from "@capacitor/core";
import { CapacitorSQLite, SQLiteConnection } from "@capacitor-community/sqlite";
import {
	defineCustomElements as jeepSqlite,
} from "jeep-sqlite/loader";

const platform = Capacitor.getPlatform();
const sqlite = new SQLiteConnection(CapacitorSQLite);

// SQLite is disabled on web platform to prevent WASM memory errors
const sqliteEnabled = platform !== "web";

const createLibraryApiCache = `
	CREATE TABLE IF NOT EXISTS lib_api_cache (
    url TEXT NOT NULL,
    content TEXT NOT NULL
	);`;

const createHistoryApiCache = `
	CREATE TABLE IF NOT EXISTS history_api_cache (
		user_id INTEGER NOT NULL,
		track_id INTEGER NOT NULL,
		content TEXT NOT NULL
	);`;

const createPromosApiCache = `
	CREATE TABLE IF NOT EXISTS promos_api_cache (
		promo_url TEXT NOT NULL,
		content TEXT NOT NULL
	);`;

const createDownloadedPromosCache = `
  CREATE TABLE IF NOT EXISTS downloaded_promos_cache (
    user_id INTEGER NOT NULL,
    promo_id INTEGER NOT NULL,
    content TEXT NOT NULL
	);`;

const createDownloadedFiles = `
  CREATE TABLE IF NOT EXISTS downloaded_files (
    book_id INTEGER NOT NULL,
    track_id INTEGER NOT NULL
	);`;

const create_sync_playback_history = `
	CREATE TABLE "sync_playback_history" (
		"track_id"    INTEGER,
		"team_id"    INTEGER,
		"book_id"    INTEGER,
		"playback_rate"    REAL,
		"current_position"    REAL,
		"seconds_played_since_sync"    REAL,
		"dt_last_updated"    INTEGER,
		"promo_id"    INTEGER,
		"team_slug" TEXT,
		"promo_slug" TEXT,
		PRIMARY KEY("track_id")
	);`;

const create_all_playback_history = `
  CREATE TABLE "all_playback_history" (
    "track_id"    INTEGER,
    "team_id"    INTEGER,
    "book_id"    INTEGER,
    "playback_rate"    REAL,
    "current_position"    REAL,
    "seconds_played_since_sync"    REAL,
    "dt_last_updated"    INTEGER,
    "promo_id"    INTEGER,
    "team_slug" TEXT,
    "promo_slug" TEXT,
    PRIMARY KEY("track_id")
  );`;

async function isConnected() {
	if (!sqliteEnabled) {
		return false;
	}
	try {
		const con = await sqlite.isConnection("db", false);
		const ret = await sqlite.checkConnectionsConsistency();
		return ret.result && con.result;
	} catch (e) {
		console.warn('SQLite connection check failed:', e);
		return false;
	}
}

async function getDB() {
	if (!sqliteEnabled) {
		throw new Error('SQLite is disabled on this platform');
	}
	try {
		let db;
		const connected = await isConnected();
		if (connected) {
			db = await sqlite.retrieveConnection("db", false);
		} else {
			db = await sqlite.createConnection("db", false, "no-encryption", 1, false);
		}
		return db;
	} catch (e) {
		console.warn('SQLite getDB failed:', e);
		throw e;
	}
}

async function openDB() {
	const db = await getDB();
	await db.open();
	return db;
}

async function closeDB() {
	const db = await getDB();
	await db.close();
}

export async function queryDB(sql, params = []) {
	if (!sqliteEnabled) {
		console.warn('SQLite is disabled on this platform, returning empty result');
		return [];
	}
	try {
		const db = await getDB();
		const ret = await db.query(sql, params);
		return ret?.values || [];
	} catch (e) {
		console.warn('SQLite query failed, returning empty result:', e);
		return [];
	}
}

export async function runDB(sql, params = []) {
	if (!sqliteEnabled) {
		console.warn('SQLite is disabled on this platform, returning false');
		return false;
	}
	try {
		const db = await getDB();
		await db.run(sql, params);
		return true;
	} catch (e) {
		console.warn('SQLite run failed:', e);
		return false;
	}
}

export async function initDB() {
	if (!sqliteEnabled) {
		console.warn('SQLite is disabled on this platform, skipping initDB');
		return false;
	}
	try {
		// Don't duplicate initialization - this is handled in main.ts
		// Just ensure tables are created
		const db = await getDB();
		return await Promise.all([
			db.execute(createLibraryApiCache),
			db.execute(createHistoryApiCache),
			db.execute(createPromosApiCache),
			db.execute(createDownloadedPromosCache),
			db.execute(createDownloadedFiles),
			db.execute(create_all_playback_history),
			db.execute(create_sync_playback_history)
		]);
	} catch (e) {
		console.warn('SQLite initDB failed:', e);
		// Don't throw - allow app to continue without SQLite on web
		return false;
	}
}

export default sqlite;
